import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { InventoryItemType } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { rowsOf, useApi } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { apiMessage } from '../shared';
import { ToggleRow } from '@/components/ui/switch';
import {
  COST_SCALE,
  QUANTITY_SCALE,
  itemBody,
  itemProblem,
  type ItemDraft,
  type UnitRow,
} from './inventoryDefs';

export interface InventoryItemRow {
  id: string;
  name: string;
  sku: string | null;
  barcode?: string | null;
  description?: string | null;
  catalogItemId?: string | null;
  type: string;
  unitId?: string;
  costPrice: string;
  reorderPoint: string | null;
  reorderQuantity?: string | null;
  isActive: boolean;
  unit: { id: string; code: string; name: string } | null;
}

export type ItemTarget = InventoryItemRow | 'new';

const ITEM_TYPES: InventoryItemType[] = Object.values(InventoryItemType);

const EMPTY_ITEM: ItemDraft = {
  name: '',
  unitId: '',
  type: InventoryItemType.RAW_MATERIAL,
  catalogItemId: '',
  sku: '',
  barcode: '',
  description: '',
  costPrice: '',
  reorderPoint: '',
  reorderQuantity: '',
  isActive: true,
};

interface Props {
  target: ItemTarget | null;
  units: UnitRow[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * X-16 — defining an inventory item.
 *
 * This is the gap that made the rest of the screen theatre: five kinds of stock
 * movement could be posted, and `RecipeComponentPicker` could pick, but nothing
 * anywhere could add a row to the list they all read from. demo-cafe has two
 * items, both from the seed.
 */
export function InventoryItemDialog({ target, units, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ItemDraft>(EMPTY_ITEM);
  const [busy, setBusy] = useState(false);
  const editing = target && target !== 'new' ? target : null;

  /**
   * Only fetched while the dialog is open. `catalogItemId` is the optional link
   * for an item sold exactly as it is stocked (a bottle of water), and pulling
   * the whole catalogue on every page load to fill a field most items leave
   * blank is not worth a request.
   */
  const catalog = useApi<unknown>(target ? '/catalog' : null, { limit: 200 });
  const catalogRows = rowsOf<{ id: string; name: string }>(catalog.data).rows;

  useEffect(() => {
    if (!target) return;
    setDraft(
      target === 'new'
        ? EMPTY_ITEM
        : {
            name: target.name,
            unitId: target.unitId ?? target.unit?.id ?? '',
            type: (target.type as InventoryItemType) ?? InventoryItemType.RAW_MATERIAL,
            catalogItemId: target.catalogItemId ?? '',
            sku: target.sku ?? '',
            barcode: target.barcode ?? '',
            description: target.description ?? '',
            costPrice: target.costPrice ?? '',
            reorderPoint: target.reorderPoint ?? '',
            reorderQuantity: target.reorderQuantity ?? '',
            isActive: target.isActive,
          },
    );
  }, [target]);

  /**
   * Only ACTIVE units are offered for a new item's stock unit. A retired unit
   * is still perfectly legal on the item that already carries it — which is why
   * an existing item's own unit is kept in the list even when retired, rather
   * than silently disappearing out of a form and being resubmitted as blank.
   */
  const unitOptions = useMemo(() => {
    const current = draft.unitId;
    return units.filter((unit) => unit.isActive || unit.id === current);
  }, [units, draft.unitId]);

  const problem = useMemo(() => itemProblem(draft), [draft]);

  const submit = async () => {
    if (problem) return;
    setBusy(true);
    try {
      const body = itemBody(draft);
      if (editing) await api.patch(`/inventory/items/${editing.id}`, body);
      else await api.post('/inventory/items', body);
      toast.success(
        t(
          editing
            ? 'operations.inventory.defs.items.updated'
            : 'operations.inventory.defs.items.created',
          { name: draft.name.trim() },
        ),
      );
      onSaved();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const fieldError = (field: string) =>
    problem?.field === field
      ? t(`operations.inventory.defs.items.errors.${problem.code}`, {
          scale: field === 'costPrice' ? COST_SCALE : QUANTITY_SCALE,
        })
      : undefined;

  const unitCode = units.find((unit) => unit.id === draft.unitId)?.code ?? '';

  return (
    <Dialog
      open={!!target}
      onClose={onClose}
      title={t(
        editing ? 'operations.inventory.defs.items.edit' : 'operations.inventory.defs.items.new',
      )}
      description={t('operations.inventory.defs.items.formHint')}
      className="max-w-3xl"
      footer={
        <>
          <Button variant="ghost" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit} disabled={busy || !!problem}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('operations.inventory.defs.items.name')}
          required
          error={fieldError('name')}
        >
          <Input
            autoFocus
            className="h-12"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={t('operations.inventory.defs.items.namePlaceholder')}
          />
        </Field>

        {/*
          The stock unit is the one field that cannot be shrugged off: every
          movement of this item is stored in it, and changing it later does NOT
          restate the ledger — `StockService` converts an incoming quantity into
          whatever the item's unit is *at that moment*, so past rows keep the
          old meaning. Hence the warning when editing rather than a free swap.
        */}
        <Field
          label={t('operations.inventory.defs.items.unit')}
          required
          error={fieldError('unitId')}
          hint={
            editing
              ? t('operations.inventory.defs.items.unitChangeHint')
              : t('operations.inventory.defs.items.unitHint')
          }
        >
          <Select
            className="h-12"
            value={draft.unitId}
            onChange={(e) => setDraft({ ...draft, unitId: e.target.value })}
          >
            <option value="">{t('operations.filters.choose')}</option>
            {unitOptions.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name} ({unit.code})
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('operations.inventory.itemType')}>
          <Select
            className="h-12"
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value as InventoryItemType })}
          >
            {ITEM_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`operations.inventory.itemTypes.${type}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('operations.inventory.defs.items.sku')}
          hint={t('operations.inventory.defs.items.skuHint')}
        >
          <Input
            className="h-12 font-mono"
            value={draft.sku}
            onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
          />
        </Field>

        <Field label={t('operations.inventory.defs.items.barcode')}>
          <Input
            className="h-12 font-mono"
            value={draft.barcode}
            onChange={(e) => setDraft({ ...draft, barcode: e.target.value })}
          />
        </Field>

        <Field
          label={t('operations.inventory.defs.items.catalogItem')}
          hint={t('operations.inventory.defs.items.catalogItemHint')}
        >
          <Select
            className="h-12"
            value={draft.catalogItemId}
            onChange={(e) => setDraft({ ...draft, catalogItemId: e.target.value })}
          >
            <option value="">{t('operations.inventory.defs.items.notSold')}</option>
            {catalogRows.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </Select>
        </Field>

        {/*
          NOT money, and deliberately not formatted by `useCurrency()`.
          `inventory_items.cost_price` is `Decimal(12,4)` — a cost per stock
          unit. Flour is 0.0020 per gram; rounded to a currency's two places it
          is zero, and a valuation built on it is zero too.
        */}
        <Field
          label={t('operations.inventory.defs.items.costPrice')}
          error={fieldError('costPrice')}
          hint={t('operations.inventory.defs.items.costPriceHint', {
            unit: unitCode || t('operations.inventory.defs.items.stockUnit'),
            scale: COST_SCALE,
          })}
        >
          <Input
            className="h-12"
            inputMode="decimal"
            dir="ltr"
            value={draft.costPrice}
            onChange={(e) => setDraft({ ...draft, costPrice: e.target.value })}
            placeholder="0.0020"
          />
        </Field>

        <Field
          label={t('operations.inventory.reorderPoint')}
          error={fieldError('reorderPoint')}
          hint={t('operations.inventory.defs.items.reorderPointHint')}
        >
          <Input
            className="h-12"
            inputMode="decimal"
            dir="ltr"
            value={draft.reorderPoint}
            onChange={(e) => setDraft({ ...draft, reorderPoint: e.target.value })}
            placeholder="5.000"
          />
        </Field>

        <Field
          label={t('operations.inventory.defs.items.reorderQuantity')}
          error={fieldError('reorderQuantity')}
          hint={t('operations.inventory.defs.items.reorderQuantityHint')}
        >
          <Input
            className="h-12"
            inputMode="decimal"
            dir="ltr"
            value={draft.reorderQuantity}
            onChange={(e) => setDraft({ ...draft, reorderQuantity: e.target.value })}
            placeholder="20.000"
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label={t('operations.inventory.defs.items.description')}>
            <Textarea
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <ToggleRow
            checked={draft.isActive}
            onChange={(isActive) => setDraft({ ...draft, isActive })}
            label={t('operations.inventory.defs.active')}
            hint={t('operations.inventory.defs.items.isActiveHint')}
          />
        </div>
      </div>
    </Dialog>
  );
}
